// UserContext.js
import React, { createContext, useState } from 'react';
import Variables from '../Variables.json';

export const UserContext = createContext(null);
export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState({});
  const [metaData, setMetaData] = useState({});
  const [wishlistItems, setWishlistItems] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authType, setAuthType] = useState("login");

  const baseUrl = Variables.baseUrl;

  // Function to load wishlist items
  const loadWishlistItems = async (userId) => {
    try {
      const formData = new FormData();
      formData.append('UserId', userId.toString());

      const response = await fetch(`${baseUrl}/api/Products/GetAllWishListItemsbasedOnUserId`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json();

      if (Array.isArray(data)) {
        setWishlistItems(data);
      }
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    }
  };

  // Function to add item to wishlist
  const addToWishlist = async (product) => {
    // 1. Update local state immediately (Optimistic UI)
    const exists = wishlistItems.some(item => item.Productid === product.Productid);
    if (!exists) {
      setWishlistItems(prev => [...prev, product]);

      // 2. If logged in, save to API
      if (userData.isLoggedIn && userData.id) {
        try {
          const payload = [{
            UserId: userData.id.toString(),
            ProductId: product.Productid.toString(),
            Quantity: "1",
            Price: (product.Price1 || product.Price || "0").toString()
          }];

          const formData = new FormData();
          formData.append('Cartxml', JSON.stringify(payload));

          await fetch(`${baseUrl}/api/Products/Insert_WishList_Items`, {
            method: 'POST',
            body: formData
          });
          // Build a bridge: Fetch again to sync perfectly or just trust the push
          // For now, trusting the push is faster.
        } catch (error) {
          console.error("Error saving wishlist item details to server:", error);
        }
      } else {
        // Not logged in: Show login modal? Or just keep local?
        // Existing behavior is just keep local (until refresh).
        // Ideally we prompt login, but let's stick to current flow + API save if possible.
        setShowAuthModal(true);
        setAuthType("login");
      }
    } else {
      // If it exists, currently we (or the UI) might want to remove it.
      // The previous toggle logic was in the pages.
      // We can handle remove here too if we want a full toggle function.
      removeFromWishlist(product.Productid);
    }
  };

  const removeFromWishlist = async (productId) => {
    // Determine the item to remove
    const itemToRemove = wishlistItems.find(item => item.Productid === productId);

    // Update local state immediately
    setWishlistItems(prev => prev.filter(item => item.Productid !== productId));

    if (itemToRemove && userData.isLoggedIn && userData.id) {
      try {
        // Priority: 'WishlistId', then 'Id' (if distinct from ProductId), else try ProductId?
        // The API requires 'wishListid'.
        // If the fetched item has an 'Id' that is NOT the ProductId, that's likely the WishlistId.
        // Or if it explicitly has 'WishlistId'.
        const wishlistId = itemToRemove.WishlistId || itemToRemove.Id || itemToRemove.wishlistId;

        if (wishlistId) {
          const formData = new FormData();
          formData.append('wishListid', wishlistId.toString());

          await fetch(`${baseUrl}/api/Products/DeleteWishlistItems`, {
            method: 'POST',
            body: formData
          });
        } else {
          console.warn("Could not find WishlistId for deletion:", itemToRemove);
        }
      } catch (error) {
        console.error("Error deleting wishlist item:", error);
      }
    }
  };

  return (
    <UserContext.Provider value={{
      userData, setUserData,
      metaData, setMetaData,
      wishlistItems, setWishlistItems,
      cartItems, setCartItems,
      showAuthModal, setShowAuthModal,
      authType, setAuthType,
      addToWishlist, removeFromWishlist, loadWishlistItems
    }}>
      {children}
    </UserContext.Provider>
  );
};